import { defineStore } from "pinia";
import { computed, watch } from "vue";
import {
  andThenMaybe,
  isJust,
  isNothing,
  just,
  mapMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { useTea } from "../lib/tea";
import { parseMapDocument, type MapDocument } from "../lib/mapDocument";
import { joinProjectPath } from "../lib/runtime";
import { buildStoryboardMapLocationIndex } from "../lib/storyboardMapLocations";
import { readProjectAssetDataUrl } from "../lib/tauri/assets";
import { readProjectFile } from "../lib/tauri/core";
import { useOutlineEntitiesStore } from "./useOutlineEntitiesStore";
import { PROJECT_DATABASE_DIR } from "../lib/zwConfig";
import { useEditorSessionStore } from "./useEditorSessionStore";
import {
  initialModel,
  update,
  type CachedTimelineMap,
  type Cmd,
} from "./useTimelineMapsStore.update";

/**
 * Session cache of map locations for the timeline. The first open of a project
 * reads each `.zwmap` once. Later visits reuse that index. A map save patches
 * the one file that changed.
 */
export const useTimelineMapsStore = defineStore("timelineMaps", () => {
  const session = useEditorSessionStore();
  const entities = useOutlineEntitiesStore();
  const { model, dispatch } = useTea(initialModel, update, {
    runCmd: just(load),
  });

  async function load(cmd: Cmd): Promise<void> {
    try {
      await entities.refreshDatabase();
      const entries = entities.databaseEntryFields;
      const loaded = await Promise.all(
        entries
          .filter((entry) => entry.isMap)
          .map(async (entry) => {
            try {
              const raw = await readProjectFile(
                joinProjectPath(
                  cmd.root,
                  `${PROJECT_DATABASE_DIR}/${entry.name}`,
                ),
              );
              const doc = parseMapDocument(raw);
              const assets = await loadAssets(cmd.root, doc, nothing);
              return just({ name: entry.name, doc, assets });
            } catch {
              return nothing;
            }
          }),
      );
      const maps = loaded.flatMap((value) =>
        isJust(value) ? [value.value] : [],
      );
      await decodePreviews(maps);
      await dispatch({ type: "Loaded", generation: cmd.generation, maps });
    } catch {
      await dispatch({ type: "Failed", generation: cmd.generation });
    }
  }

  async function noteMapSaved(name: string, raw: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const doc = parseMapDocument(raw);
    const previous = toMaybe(model.value.maps.find((map) => map.name === name));
    const assets = await loadAssets(
      session.rootPath.value,
      doc,
      mapMaybe(previous, (map) => map.assets),
    );
    const map: CachedTimelineMap = { name, doc, assets };
    await decodePreviews([map]);
    await dispatch({ type: "MapUpdated", map });
  }

  async function noteMapRemoved(name: string): Promise<void> {
    await dispatch({ type: "MapRemoved", name });
  }

  async function noteMapRenamed(from: string, to: string): Promise<void> {
    await dispatch({ type: "MapRenamed", from, to });
  }

  async function refresh(): Promise<void> {
    await dispatch({ type: "Load", root: session.rootPath });
  }
  watch(
    () => (isJust(session.rootPath) ? session.rootPath.value : ""),
    () => void refresh(),
    { immediate: true },
  );
  return {
    index: computed(() => model.value.index),
    ready: computed(() => model.value.ready),
    refresh,
    noteMapSaved,
    noteMapRemoved,
    noteMapRenamed,
  };
});

function assetPaths(doc: MapDocument): Set<string> {
  const paths = new Set(
    doc.layers.flatMap((layer) => layer.images.map((image) => image.path)),
  );
  if (typeof doc.landmass !== "undefined") paths.add(doc.landmass.path);
  return paths;
}

async function loadAssets(
  root: string,
  doc: MapDocument,
  previous: Maybe<ReadonlyMap<string, string>>,
): Promise<Map<string, string>> {
  const assets = new Map<string, string>();
  await Promise.all(
    [...assetPaths(doc)].map(async (path) => {
      const cached = andThenMaybe(previous, (known) =>
        toMaybe(known.get(path)),
      );
      if (isJust(cached)) {
        assets.set(path, cached.value);
        return;
      }
      try {
        assets.set(path, await readProjectAssetDataUrl(root, path));
      } catch {
        /* Vector previews remain available if an image is missing. */
      }
    }),
  );
  return assets;
}

async function decodePreviews(
  maps: readonly CachedTimelineMap[],
): Promise<void> {
  const index = buildStoryboardMapLocationIndex(
    maps.map((map) => ({
      name: map.name,
      doc: map.doc,
      assets: just(map.assets),
    })),
  );
  const previews = new Set(
    [...index.values()].flatMap((chips) =>
      chips.flatMap((chip) =>
        isJust(chip.previewSrc) ? [chip.previewSrc.value] : [],
      ),
    ),
  );
  await Promise.all(
    [...previews].map(async (src) => {
      const image = new Image();
      image.src = src;
      try {
        if (typeof image.decode === "function") await image.decode();
      } catch {
        /* A broken preview must not prevent access to the timeline. */
      }
    }),
  );
}
